import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth/useAuth';
import type {
  FormationProgressKpi,
  FormationSignature,
  FormationWithRelations,
  SignatoryRole,
} from './types';

const SELECT_WITH_RELATIONS = `
  *,
  employee:employees!employee_id(id, first_name, last_name),
  trainer:employees!trainer_id(id, first_name, last_name),
  template:formation_templates!template_id(id, position_code, position_label)
` as const;

export interface PendingSignatureItem {
  formation: FormationWithRelations;
  /** Quel(s) rôle(s) je dois signer sur cette formation. */
  pendingRoles: SignatoryRole[];
}

/**
 * Calcule les formations à 100% dont une de mes signatures est manquante.
 *
 * Je suis "concerné" si :
 *   - Je suis l'employé concerné       → role 'employee'
 *   - Je suis le formateur de la fiche → role 'trainer'
 *   - J'ai la perm formation.manage    → role 'manager'
 *
 * Pour chaque formation in_progress à 100%, on regarde quelles signatures
 * me reviennent et lesquelles n'ont pas été déposées.
 */
export function usePendingSignatures() {
  const { employee, hasPermission } = useAuth();
  const myEmployeeId = employee?.id ?? null;
  const canBeManager = hasPermission('formation.manage');

  return useQuery({
    queryKey: ['formations', 'pending-signatures', myEmployeeId, canBeManager],
    enabled: !!myEmployeeId || canBeManager,
    queryFn: async (): Promise<PendingSignatureItem[]> => {
      // 1) Toutes les formations in_progress (RLS filtre déjà selon mon rôle)
      const { data: formationsRaw, error: fErr } = await supabase
        .from('formations')
        .select(SELECT_WITH_RELATIONS)
        .eq('status', 'in_progress');
      if (fErr) throw fErr;
      const formations = (formationsRaw ?? []) as unknown as FormationWithRelations[];
      if (formations.length === 0) return [];

      const ids = formations.map((f) => f.id);

      // 2) KPI → filtrer sur 100%
      const { data: kpisRaw, error: kErr } = await supabase
        .from('v_formation_progress')
        .select('formation_id, progression_pct')
        .in('formation_id', ids);
      if (kErr) throw kErr;
      const kpiMap = new Map<string, number>();
      for (const k of (kpisRaw ?? []) as Pick<
        FormationProgressKpi,
        'formation_id' | 'progression_pct'
      >[]) {
        kpiMap.set(k.formation_id, k.progression_pct);
      }

      // 3) Signatures déjà déposées
      const { data: sigsRaw, error: sErr } = await supabase
        .from('formation_signatures')
        .select('formation_id, signatory_role')
        .in('formation_id', ids);
      if (sErr) throw sErr;
      const signedSet = new Set<string>();
      for (const s of (sigsRaw ?? []) as Pick<
        FormationSignature,
        'formation_id' | 'signatory_role'
      >[]) {
        signedSet.add(`${s.formation_id}:${s.signatory_role}`);
      }

      // 4) Pour chaque formation à 100%, calcule mes rôles en attente
      const result: PendingSignatureItem[] = [];
      for (const f of formations) {
        const pct = kpiMap.get(f.id) ?? 0;
        if (pct < 100) continue;

        const myPendingRoles: SignatoryRole[] = [];

        const isEmployee = !!myEmployeeId && f.employee_id === myEmployeeId;
        const isTrainer = !!myEmployeeId && f.trainer_id === myEmployeeId;

        if (isEmployee && !signedSet.has(`${f.id}:employee`)) {
          myPendingRoles.push('employee');
        }
        if (isTrainer && !signedSet.has(`${f.id}:trainer`)) {
          myPendingRoles.push('trainer');
        }
        if (canBeManager && !signedSet.has(`${f.id}:manager`)) {
          myPendingRoles.push('manager');
        }

        if (myPendingRoles.length > 0) {
          result.push({ formation: f, pendingRoles: myPendingRoles });
        }
      }

      // Tri : plus récent démarrage en premier
      result.sort(
        (a, b) =>
          new Date(b.formation.started_at).getTime() -
          new Date(a.formation.started_at).getTime()
      );
      return result;
    },
    staleTime: 30_000, // 30s : pas critique, refresh raisonnable
  });
}
